import styled from "@emotion/styled";
import React, { FC, useEffect } from "react";
import {
  StarIcon,
  GoalCheckIcon,
  InfoCircleIcon,
  ConsistencyCheckIcon,
  IconArrowNarrowRight
} from "src/constants/icons";
import {
  AppPopup,
  AppQueryPopupsController
} from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import Translate from "src/ui/components/Translate/Translate";
import translate from "src/lib/translate";
import ConsistencyScoreCubit, {
  ConsistencyScoreStatus,
  RequirementFulfillmentType,
  ConsistencyScoreGoal
} from "src/state/ConsistencyScoreCubit/ConsistencyScoreCubit";
import { useBlocNext } from "@blac/react";
import { useBloc } from "src/state/state";
import { featureFlags } from "src/lib/featureFlags";
import SubscriptionCubit from "src/state/SubscriptionCubit/SubscriptionCubit";
import { SubscriptionDetailsResponse } from "@9amhealth/openapi";
import { SubscriptionInterval } from "src/state/TreatmentPlanCubit/TreatmentPlanCubit";

export interface ConsistencyScoreTileProps {
  simple?: boolean;
  returnUrl?: string;
}

const TileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: var(--light-shadow);
`;

const MainContainer = styled.div`
  display: flex;
  align-items: center;
`;

const GoalsSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoButton = styled.button`
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(170, 167, 160, 0.2);
  }

  svg {
    width: 16px;
    height: 16px;
    color: #aaa7a0;
  }
`;

const GoalItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--greys-charcoal-80);
`;

const GoalItemContainer = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const Divider = styled.div`
  height: 1px;
  background-color: var(--color-gray-lighter);
  width: calc(100% + 2rem);
  margin: 0 -1rem;
`;

const GoalIndicatorContainer = styled.div`
  position: absolute;
  top: -5px;
  z-index: 1;
`;

const GoalIndicator = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e6e3db;

  &[data-achieved="true"] {
    background: #6fc696;
  }
`;

const GoalText = styled.span`
  flex: 0 1 auto;
`;

const CheckIcon = styled.span`
  color: #e6e3db;
  display: flex;
  align-items: center;
  justify-content: center;

  &[data-achieved="true"] {
    color: #6fc696;
  }
`;

const ProgressSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  position: relative;

  &[data-simple="true"] {
    width: 62px;
    height: 62px;
  }
`;

const ProgressCircle = styled.svg`
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
`;

const CircleBackground = styled.circle`
  fill: none;
  stroke: var(--greys-light-gray, #e6e3db);
  stroke-width: 3;
`;

const CircleProgress = styled.circle`
  fill: none;
  stroke: var(--progress-color, #6fc696);
  stroke-width: 3;
  stroke-linecap: round;
  transition: stroke-dasharray 0.3s ease;

  &[data-status="PERFECT"] {
    stroke: url(#rainbowGradient);
  }
`;

const LearnMoreButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  line-height: 1;
`;

const ScoreText = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-weight: 700;
  font-size: 1rem;
  color: var(--progress-color, #6fc696);
  margin: 0;
`;

const getProgressColor = (status: keyof typeof ConsistencyScoreStatus) => {
  switch (status) {
    case ConsistencyScoreStatus.GOOD:
      return "#6FC696";
    case ConsistencyScoreStatus.MEDIUM:
      return "var(--color-sunrise-orange)";
    case ConsistencyScoreStatus.LOW:
      return "#FF7663";
    case ConsistencyScoreStatus.NONE:
    case ConsistencyScoreStatus.PERFECT:
    default:
      return "transparent";
  }
};

const getGoalDisplayText = (goal: ConsistencyScoreGoal) => {
  return `${goal.title} (${Math.min(goal.count, goal.maxCount)}/${goal.maxCount})`;
};

export const ConsistencyScoreTile: FC<ConsistencyScoreTileProps> = ({
  simple = false,
  returnUrl = ""
}) => {
  const [{ data }, cubit] = useBlocNext(ConsistencyScoreCubit);

  useEffect(() => {
    cubit.refresh();
  }, []);

  const [, { filterAllSubscriptions }] = useBloc(SubscriptionCubit);

  const isSubscriptionPaused = Boolean(
    filterAllSubscriptions({
      status: [SubscriptionDetailsResponse.status.PAUSED],
      duration: [SubscriptionInterval.monthly, SubscriptionInterval.quarterly]
    }).length
  );

  if (!featureFlags.consistencyScoreEnabled || !data || isSubscriptionPaused) {
    return null;
  }

  const { score = 0, status, goals, requirementFulfillments } = data;
  const progressColor = getProgressColor(status);
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = `${(score / 100) * circumference} ${circumference}`;
  const isGoalAchieved = requirementFulfillments.some(
    (fulfillment) =>
      fulfillment.type === RequirementFulfillmentType.GLP1_REFILL &&
      fulfillment.fulfilled
  );

  const openInfoPopup = () => {
    AppQueryPopupsController.openPopup(AppPopup.consistencyScoreInfo, {
      additionalParameters: { returnUrl }
    });
  };

  return (
    <TileContainer>
      <MainContainer>
        <GoalsSection>
          <TitleContainer>
            <h5 className="as-body2">
              <Translate msg="consistency_score.title" />
            </h5>
            {simple && (
              <InfoButton
                onClick={openInfoPopup}
                aria-label={translate("consistency_score.learn_more_aria")}
              >
                <InfoCircleIcon />
              </InfoButton>
            )}
          </TitleContainer>
          {simple ? (
            <p className="as-little color-c-80 m0">
              <Translate msg="consistency_score.simple_description" />
            </p>
          ) : (
            <GoalItemContainer>
              {goals.map((goal) => (
                <GoalItem key={goal.title}>
                  <CheckIcon data-achieved={goal.count >= goal.maxCount}>
                    <ConsistencyCheckIcon data-testid="checkmark-icon" />
                  </CheckIcon>
                  <GoalText>{getGoalDisplayText(goal)}</GoalText>
                </GoalItem>
              ))}
            </GoalItemContainer>
          )}
        </GoalsSection>

        <ProgressSection data-simple={simple} style={{ position: "relative" }}>
          {simple && (
            <GoalIndicatorContainer>
              <GoalIndicator
                data-testid="goal-indicator"
                data-achieved={isGoalAchieved}
              >
                {isGoalAchieved ? (
                  <GoalCheckIcon data-testid="goal-check-icon" />
                ) : (
                  <StarIcon />
                )}
              </GoalIndicator>
            </GoalIndicatorContainer>
          )}
          <ProgressCircle viewBox="0 0 72 72">
            <defs>
              <linearGradient
                id="rainbowGradient"
                x1="62"
                y1="32"
                x2="0"
                y2="32"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#80AEFF" />
                <stop offset="0.526781" stopColor="#F7BDE6" />
                <stop offset="1" stopColor="#FFBD70" />
              </linearGradient>
            </defs>
            <CircleBackground cx="36" cy="36" r={radius} />
            <CircleProgress
              cx="36"
              cy="36"
              r={radius}
              strokeDasharray={strokeDasharray}
              data-status={status}
              data-testid="progress-circle"
              style={{
                stroke:
                  status === ConsistencyScoreStatus.PERFECT
                    ? undefined
                    : progressColor
              }}
            />
          </ProgressCircle>
          <ScoreText
            style={{
              color:
                status === ConsistencyScoreStatus.PERFECT
                  ? "var(--color-charcoal-80)"
                  : progressColor
            }}
          >
            {score}
          </ScoreText>
        </ProgressSection>
      </MainContainer>

      {!simple && (
        <>
          <Divider />

          <LearnMoreButton
            className="as-little strong"
            onClick={openInfoPopup}
            aria-label={translate("consistency_score.learn_more_aria")}
          >
            <Translate msg="consistency_score.learn_more" />
            <IconArrowNarrowRight />
          </LearnMoreButton>
        </>
      )}
    </TileContainer>
  );
};

export default ConsistencyScoreTile;
